import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Users, Eye, Star, Check, Compass, AlertTriangle, ShieldAlert } from "lucide-react";

const STAGE_META = {
  primary: { label: "Primary county providers", Icon: Star, tone: "text-amber-600" },
  also: { label: "Also-willing + no-preference providers (widened)", Icon: Check, tone: "text-indigo-600" },
  admin: { label: "Admin escalation", Icon: AlertTriangle, tone: "text-red-600" },
};

// Read-only preview of who a notary order's routing WOULD notify next. Sends nothing.
export const RoutingRecipientPreview = ({ orderId, jobNumber }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/orders/${orderId}/routing-preview`);
      setData(data);
    } catch {
      setData({ error: true });
    } finally {
      setLoading(false);
    }
  };

  const onOpenChange = (v) => { setOpen(v); if (v) load(); };
  const sm = data && data.stage ? STAGE_META[data.stage] : null;
  const SIcon = sm?.Icon || Compass;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`routing-preview-btn-${jobNumber}`} onClick={(e) => e.stopPropagation()}>
          <Eye className="mr-1 h-3.5 w-3.5" /> Preview recipients
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="routing-preview-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-indigo-500" /> Routing preview — #{jobNumber}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-400">Read-only. This sends nothing. Routing begins when you approve the order.</p>
        {loading && <p className="py-6 text-center text-sm text-slate-400">Loading…</p>}
        {data && data.error && <p className="py-6 text-center text-sm text-red-500">Could not load preview.</p>}
        {data && !data.error && !data.applicable && (
          <p className="py-6 text-center text-sm text-slate-500" data-testid="routing-preview-na">Not a notary order — county routing does not apply.</p>
        )}
        {data && !data.error && data.applicable && (
          <div className="space-y-3">
            {!data.notifications_enabled && (
              <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs font-semibold text-amber-800" data-testid="routing-preview-killswitch-warn">
                <ShieldAlert className="h-4 w-4" /> Routing notifications are currently OFF (kill switch). Nobody will be notified — the pool stays claimable.
              </div>
            )}
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <span className={`flex items-center gap-1.5 text-sm font-semibold ${sm?.tone || "text-slate-600"}`}>
                  <SIcon className="h-4 w-4" /> {sm ? sm.label : "No one to notify"}
                </span>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700" data-testid="routing-preview-count">{data.count} recipient{data.count === 1 ? "" : "s"}</span>
              </div>
              <p className="mt-1 text-xs text-slate-400">County: {data.county || data.county_fips || (data.needs_manual_county ? "needs manual county" : "—")}</p>
              <ul className="mt-2 max-h-56 space-y-1 overflow-y-auto" data-testid="routing-preview-list">
                {data.recipients.map((r, i) => (
                  <li key={r.id || r.email || i} className="flex items-center justify-between rounded bg-slate-50 px-2 py-1 text-sm">
                    <span className="text-slate-800">{r.name || "(unnamed)"}</span>
                    {r.email && <span className="text-xs text-slate-400">{r.email}</span>}
                  </li>
                ))}
                {data.recipients.length === 0 && <li className="px-2 py-3 text-center text-xs text-slate-400">No eligible recipients at any stage — this order would escalate straight to admin with nobody in the provider tiers.</li>}
              </ul>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
